'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  Home, 
  Users, 
  Activity, 
  MessageCircle, 
  Search, 
  Crown,
  ChevronRight,
  X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DAILY_RECOMMENDATION_MENU_NAME } from '@/utils/dailyRecommendationMenu';
import DailyRecommendationMenuLabel from './DailyRecommendationMenuLabel';
import IDSearchPopup from './IDSearchPopup';
import SurnameSearchPopup from './SurnameSearchPopup';

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DropdownItem {
  name: string;
  href?: string;
  action?: string;
}

export default function MobileNavigation({ isOpen, onClose }: MobileNavigationProps) {
  const { premium } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showIDSearch, setShowIDSearch] = useState(false);
  const [showSurnameSearch, setShowSurnameSearch] = useState(false);

  const toggleDropdown = (menuName: string) => {
    setActiveDropdown(activeDropdown === menuName ? null : menuName);
  };

  const handleSearchOptionClick = (searchType: string) => {
    setActiveDropdown(null);
    onClose();
    if (searchType === 'id') {
      setShowIDSearch(true);
    } else if (searchType === 'surname') {
      setShowSurnameSearch(true);
    }
  };

  const navigationItems: Array<{
    name: string;
    icon?: React.ComponentType<{ className?: string }>;
    href?: string;
    hasDropdown: boolean;
    isDailyRecommendation?: boolean;
    dropdownItems?: DropdownItem[];
  }> = [
    {
      name: 'Home',
      icon: Home,
      href: '/dashboard',
      hasDropdown: false
    },
    {
      name: DAILY_RECOMMENDATION_MENU_NAME,
      href: '/daily-recommendations',
      hasDropdown: false,
      isDailyRecommendation: true,
    },
    {
      name: 'Matches',
      icon: Users,
      hasDropdown: true,
      dropdownItems: [
        { name: 'All Matches', href: '/all-matches' },
        { name: 'New Matches', href: '/matches/new' },
        { name: 'Caste Matches', href: '/matches/caste' },
        { name: 'City Matches', href: '/matches/city' },
        { name: 'Premium Matches', href: '/matches/premium' }
      ]
    },
    {
      name: 'Activity Matches',
      icon: Activity,
      hasDropdown: true,
      dropdownItems: [
        { name: 'Who viewed my profile', href: '/matches/viewed-me' },
        { name: 'Who shortlisted me', href: '/activity/shortlisted-me' },
        { name: 'Shortlisted by Me', href: '/activity/my-shortlist' },
        { name: 'Interest sent', href: '/activity/interest-sent' },
        { name: 'Interest received', href: '/activity/interest-received' },
        { name: 'Profiles i viewed', href: '/activity/profiles-viewed' }
      ]
    },
    {
      name: 'Messages',
      icon: MessageCircle,
      href: '/messages',
      hasDropdown: false
    },
    {
      name: 'Search',
      icon: Search,
      hasDropdown: true,
      dropdownItems: [
        { name: 'Advance Search', href: '/search-results' },
        { name: 'Surname Search', action: 'surname' },
        { name: 'ID search', action: 'id' }
      ]
    },
    // Hide membership for premium users
    ...(premium?.isPremium ? [] : [{
      name: 'Membership',
      icon: Crown,
      href: '/membership',
      hasDropdown: false
    }])
  ];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[65] bg-black bg-opacity-50"
          onClick={onClose}
        />
      )}

      {/* Slide-in menu */}
      <div className={`fixed top-0 left-0 z-[70] h-full w-80 transform bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-[350px]'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Menu</h2>
          <button
            onClick={onClose}
            type="button"
            aria-label="Close menu"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto">
          <nav className="p-4">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <div key={item.href ?? item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => toggleDropdown(item.name)}
                        className="flex items-center justify-between w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          {item.icon ? <item.icon className="w-5 h-5" /> : null}
                          <span className="font-medium">{item.name}</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-transform ${
                          activeDropdown === item.name ? 'rotate-90' : ''
                        }`} />
                      </button>
                      
                      {/* Dropdown Items */}
                      {activeDropdown === item.name && (
                        <div className="ml-8 mt-2 space-y-3">
                          {item.dropdownItems?.map((dropdownItem) => (
                            dropdownItem.href ? (
                              <Link
                                key={dropdownItem.name}
                                href={dropdownItem.href}
                                onClick={onClose}
                                className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-primary-600 rounded-lg transition-colors"
                              >
                                {dropdownItem.name}
                              </Link>
                            ) : (
                              <button
                                key={dropdownItem.name}
                                onClick={() => handleSearchOptionClick(dropdownItem.action!)}
                                className="block w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-primary-600 rounded-lg transition-colors text-left"
                              >
                                {dropdownItem.name}
                              </button>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.href!}
                      onClick={onClose}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {item.isDailyRecommendation ? (
                        <DailyRecommendationMenuLabel
                          iconSize="md"
                          labelClassName="font-medium"
                        />
                      ) : (
                        <>
                          {item.icon ? <item.icon className="w-5 h-5" /> : null}
                          <span className="font-medium">{item.name}</span>
                        </>
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Search Popups — above drawer/header layers */}
      <IDSearchPopup 
        isOpen={showIDSearch} 
        onClose={() => setShowIDSearch(false)} 
      />
      <SurnameSearchPopup 
        isOpen={showSurnameSearch} 
        onClose={() => setShowSurnameSearch(false)} 
      />
    </>
  );
}
